import { UserRole } from "@excel-center/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router";
import { FirstLoginModal } from "@/src/auth/components/first-login-modal";
import { useAuth } from "@/src/auth/hooks/use-auth";
import { Sidebar } from "@/src/common/components/sidebar";
import { TopBar } from "@/src/common/components/top-bar";
import { useActivityPing } from "@/src/common/hooks/use-activity-ping";
import { useTopBarExtraItems } from "@/src/common/hooks/use-top-bar-extra-items";
import { RoleSidebar } from "@/src/common/layouts/role-sidebar";
import { clsx } from "@/src/common/utils/clsx";

/** Routes that render "full-bleed" — they take over the main area and manage
 *  their own padding / scrolling. Keep this list short and intentional. */
const FULL_BLEED_PREFIXES = ["/app/messaging", "/app/ai"] as const;
/** Exact-path full-bleed: only the listed path, not its children. */
const FULL_BLEED_EXACT = new Set(["/app/feed"]);

const ROLE_LABEL_KEYS = {
	[UserRole.STUDENT]: "roles.student",
	[UserRole.STAFF]: "roles.staff",
	[UserRole.ADMIN]: "roles.admin",
	[UserRole.SUPER_ADMIN]: "roles.super_admin",
} as const;

interface UserChip {
	name: string;
	role: string;
	initials: string;
	avatarUrl: string | null;
}

function initialsFrom(first: string | null, last: string | null, email: string | null): string {
	const firstChar = first?.trim()?.[0] ?? "";
	const lastChar = last?.trim()?.[0] ?? "";
	const pair = `${firstChar}${lastChar}`.toUpperCase();
	if (pair) return pair;
	return email?.trim()?.[0]?.toUpperCase() ?? "?";
}

/**
 * Authenticated shell — sidebar (272px desktop, off-canvas drawer below
 * `md`) + top bar + outlet.
 *
 * The sidebar tree is role-scoped and inlined via `<RoleSidebar />`
 * (see PRD §5 for the full mapping). The user chip pulls name/role/
 * initials from `useAuth().profile`, falling back gracefully while
 * the profile hydrates.
 */
export function AuthedLayout() {
	const { t } = useTranslation("common");
	const { profile, role } = useAuth();
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const location = useLocation();
	useActivityPing();

	const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
	const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

	// Auto-close the mobile drawer on route change.
	useEffect(() => {
		closeSidebar();
	}, [location.key, closeSidebar]);

	const userChip = useMemo<UserChip | undefined>(() => {
		if (!profile) return undefined;
		const fullName = `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim();
		return {
			name: fullName || profile.email,
			role: role && role !== UserRole.GUEST ? t(ROLE_LABEL_KEYS[role]) : "",
			initials: initialsFrom(profile.first_name, profile.last_name, profile.email),
			avatarUrl: profile.avatar_url,
		};
	}, [profile, role, t]);

	const topBarUser = userChip ? { name: userChip.name, initials: userChip.initials, avatarUrl: userChip.avatarUrl } : undefined;
	const extraItems = useTopBarExtraItems(role);
	const hideSettingsItem = role === UserRole.STUDENT;
	const isFullBleed = FULL_BLEED_PREFIXES.some((prefix) => location.pathname.startsWith(prefix)) || FULL_BLEED_EXACT.has(location.pathname);

	return (
		<div className="flex h-screen bg-surface">
			<Sidebar user={userChip} isOpen={isSidebarOpen} onClose={closeSidebar}>
				<RoleSidebar role={role} />
			</Sidebar>
			<div className="flex min-w-0 flex-1 flex-col">
				<TopBar
					withBrand
					brandMobileOnly
					user={topBarUser}
					extraItems={extraItems}
					hideSettings={hideSettingsItem}
					onMenuClick={openSidebar}
				/>
				<main className={clsx("flex min-h-0 flex-1 flex-col", isFullBleed ? "overflow-hidden" : "overflow-y-auto p-4 md:p-6 lg:p-8")}>
					<Outlet />
				</main>
			</div>
			<FirstLoginModal />
		</div>
	);
}
